<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import ButtonV4 from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TemplateFormModal from './components/TemplateFormModal.vue';
import TemplatePreviewModal from './components/TemplatePreviewModal.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const getters = useStoreGetters();

const templates = ref([]);
const loading = ref(false);
const syncing = ref(false);
const showCreateModal = ref(false);
const showPreviewModal = ref(false);
const selectedTemplate = ref(null);

const accountId = computed(() => getters.getCurrentAccountId.value);

const STATUS_COLORS = {
  approved: 'text-n-teal-11 bg-n-teal-2',
  pending: 'text-n-amber-11 bg-n-amber-2',
  rejected: 'text-n-ruby-11 bg-n-ruby-2',
  paused: 'text-n-slate-11 bg-n-slate-2',
  disabled: 'text-n-slate-11 bg-n-slate-2',
  deleted: 'text-n-slate-9 bg-n-slate-2',
};

const CATEGORY_ICONS = {
  marketing: 'i-lucide-megaphone',
  utility: 'i-lucide-wrench',
  authentication: 'i-lucide-shield-check',
};

const fetchTemplates = async () => {
  loading.value = true;
  try {
    const response = await WhatsappTemplatesAPI.getTemplates(
      accountId.value,
      props.inbox.id
    );
    templates.value = response.data || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.TEMPLATES.API.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const syncTemplates = async () => {
  syncing.value = true;
  try {
    const response = await WhatsappTemplatesAPI.syncTemplates(
      accountId.value,
      props.inbox.id
    );
    templates.value = response.data.templates || [];
    useAlert(
      t('INBOX_MGMT.TEMPLATES.API.SYNC_SUCCESS', {
        count: response.data.synced_count,
      })
    );
  } catch (error) {
    useAlert(t('INBOX_MGMT.TEMPLATES.API.SYNC_ERROR'));
  } finally {
    syncing.value = false;
  }
};

const deleteTemplate = async template => {
  if (
    !confirm(t('INBOX_MGMT.TEMPLATES.DELETE.CONFIRM', { name: template.name }))
  ) {
    return;
  }

  try {
    await WhatsappTemplatesAPI.deleteTemplate(
      accountId.value,
      props.inbox.id,
      template.id
    );
    templates.value = templates.value.filter(t => t.id !== template.id);
    useAlert(t('INBOX_MGMT.TEMPLATES.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.TEMPLATES.API.DELETE_ERROR'));
  }
};

const openCreateModal = () => {
  selectedTemplate.value = null;
  showCreateModal.value = true;
};

const openPreviewModal = template => {
  selectedTemplate.value = template;
  showPreviewModal.value = true;
};

const closePreviewModal = () => {
  showPreviewModal.value = false;
  selectedTemplate.value = null;
};

const handleTemplateCreated = () => {
  showCreateModal.value = false;
  fetchTemplates();
};

const getStatusColor = status => {
  return STATUS_COLORS[status] || 'text-n-slate-11 bg-n-slate-2';
};

const getCategoryIcon = category => {
  return CATEGORY_ICONS[category] || 'i-lucide-file-text';
};

const formatDate = dateString => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

onMounted(() => {
  fetchTemplates();
});
</script>

<template>
  <div class="gap-4 pt-8 mx-8">
    <div
      class="px-5 py-5 space-y-6 rounded-xl border shadow-sm border-n-weak bg-n-solid-2"
    >
      <!-- Header -->
      <div
        class="flex flex-col gap-4 justify-between items-start w-full md:flex-row md:items-center"
      >
        <div class="flex-1">
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.TEMPLATES.TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.TEMPLATES.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex gap-2 w-full md:w-auto">
          <ButtonV4
            sm
            ghost
            slate
            :is-loading="syncing"
            :disabled="syncing"
            class="flex gap-2 justify-center items-center flex-1 md:flex-initial"
            @click="syncTemplates"
          >
            <Icon
              icon="i-lucide-refresh-cw"
              class="w-4 h-4"
              :class="{ 'animate-spin': syncing }"
            />
            <span class="hidden sm:inline">{{
              t('INBOX_MGMT.TEMPLATES.SYNC_BUTTON')
            }}</span>
          </ButtonV4>
          <ButtonV4
            sm
            solid
            blue
            class="flex gap-2 justify-center items-center flex-1 md:flex-initial"
            @click="openCreateModal"
          >
            <Icon icon="i-lucide-plus" class="w-4 h-4" />
            <span>{{ t('INBOX_MGMT.TEMPLATES.CREATE_BUTTON') }}</span>
          </ButtonV4>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex justify-center items-center p-12 text-center"
      >
        <div class="flex flex-col items-center gap-3">
          <Icon
            icon="i-lucide-loader-2"
            class="w-8 h-8 text-n-blue-11 animate-spin"
          />
          <p class="text-sm font-medium text-n-slate-11">
            {{ t('INBOX_MGMT.TEMPLATES.LOADING') }}
          </p>
        </div>
      </div>

      <!-- Templates List -->
      <div v-else-if="templates.length > 0" class="space-y-3">
        <div
          v-for="template in templates"
          :key="template.id"
          class="p-4 rounded-lg border border-n-weak bg-n-solid-1 hover:border-n-blue-8 hover:shadow-md transition-all duration-200 cursor-pointer group"
          @click="openPreviewModal(template)"
        >
          <div class="flex justify-between items-start gap-4">
            <div class="flex gap-3 flex-1 min-w-0">
              <div
                class="flex-shrink-0 flex justify-center items-center w-10 h-10 rounded-lg bg-n-alpha-2"
              >
                <Icon
                  :icon="getCategoryIcon(template.category)"
                  class="w-5 h-5 text-n-slate-11"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex gap-2 items-center mb-1.5 flex-wrap">
                  <h3
                    class="text-sm font-semibold text-n-slate-12 truncate font-mono group-hover:text-n-blue-11 transition-colors"
                  >
                    {{ template.name }}
                  </h3>
                  <span
                    class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md whitespace-nowrap"
                    :class="getStatusColor(template.status)"
                  >
                    {{
                      t(
                        `INBOX_MGMT.TEMPLATES.STATUS.${template.status.toUpperCase()}`
                      )
                    }}
                  </span>
                  <span
                    class="opacity-0 group-hover:opacity-100 text-xs text-n-slate-11 transition-opacity"
                  >
                    <Icon icon="i-lucide-eye" class="inline w-3.5 h-3.5 mr-1" />
                    Clique para visualizar
                  </span>
                </div>
                <div
                  class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11"
                >
                  <span class="flex gap-1 items-center">
                    <Icon icon="i-lucide-tag" class="w-3 h-3" />
                    {{
                      t(
                        `INBOX_MGMT.TEMPLATES.CATEGORIES.${template.category.toUpperCase()}`
                      )
                    }}
                  </span>
                  <span class="flex gap-1 items-center">
                    <Icon icon="i-lucide-globe" class="w-3 h-3" />
                    {{ template.language }}
                  </span>
                  <span class="flex gap-1 items-center">
                    <Icon icon="i-lucide-calendar" class="w-3 h-3" />
                    {{ formatDate(template.created_at) }}
                  </span>
                </div>
                <p
                  v-if="template.rejection_reason"
                  class="flex gap-1.5 items-start mt-2 p-2 text-xs rounded-md bg-n-ruby-2 text-n-ruby-11"
                >
                  <Icon
                    icon="i-lucide-alert-circle"
                    class="flex-shrink-0 w-3.5 h-3.5 mt-0.5"
                  />
                  <span>{{ template.rejection_reason }}</span>
                </p>
              </div>
            </div>
            <div class="flex gap-1.5 flex-shrink-0">
              <button
                v-if="template.status !== 'deleted'"
                v-tooltip.top="'Excluir modelo'"
                class="p-1.5 rounded-md hover:bg-n-ruby-2 text-n-slate-11 hover:text-n-ruby-11 transition-colors"
                @click.stop="deleteTemplate(template)"
              >
                <Icon icon="i-lucide-trash-2" class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="py-12">
        <div
          class="flex flex-col justify-center items-center p-8 text-center rounded-lg border-2 border-dashed border-n-weak bg-n-alpha-1"
        >
          <div
            class="flex justify-center items-center mb-4 w-16 h-16 rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-lucide-file-text" class="w-8 h-8 text-n-slate-11" />
          </div>
          <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.TEMPLATES.NO_TEMPLATES') }}
          </h3>
          <p class="mb-4 text-xs text-n-slate-11 max-w-xs">
            {{ t('INBOX_MGMT.TEMPLATES.NO_TEMPLATES_HINT') }}
          </p>
          <div class="flex gap-2">
            <ButtonV4 sm ghost slate @click="syncTemplates">
              <Icon icon="i-lucide-refresh-cw" class="w-4 h-4 mr-1.5" />
              {{ t('INBOX_MGMT.TEMPLATES.SYNC_BUTTON') }}
            </ButtonV4>
            <ButtonV4 sm solid blue @click="openCreateModal">
              <Icon icon="i-lucide-plus" class="w-4 h-4 mr-1.5" />
              {{ t('INBOX_MGMT.TEMPLATES.CREATE_BUTTON') }}
            </ButtonV4>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Template Modal -->
    <TemplateFormModal
      v-if="showCreateModal"
      :inbox="inbox"
      @close="showCreateModal = false"
      @created="handleTemplateCreated"
    />

    <!-- Preview Template Modal -->
    <TemplatePreviewModal
      v-if="showPreviewModal && selectedTemplate"
      :template="selectedTemplate"
      @close="closePreviewModal"
    />
  </div>
</template>
